import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Bookmark,
  BookmarkCheck,
  CheckCircle,
  MessageCircle,
  Lightbulb,
  Users,
  ChevronDown,
} from 'lucide-react';
import { getScenarioById, getScenariosByCategory } from '../data/scenarios';
import { getCategoryById } from '../data/categories';
import { useAppStore } from '../store/appStore';

export default function ScenarioPage() {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();

  const appMode = useAppStore((s) => s.appMode);
  const user = useAppStore((s) => s.user);
  const toggleSavedScenario = useAppStore((s) => s.toggleSavedScenario);
  const markScenarioPracticed = useAppStore((s) => s.markScenarioPracticed);

  const [revealed, setRevealed] = useState(false);
  const [practiced, setPracticed] = useState(false);

  const scenario = scenarioId ? getScenarioById(scenarioId) : undefined;
  const category = scenario ? getCategoryById(scenario.categoryId) : undefined;

  const isKidMode = appMode.mode === 'kid';
  const isSaved = user?.savedScenarioIds.includes(scenarioId ?? '') ?? false;

  const categoryScenarios = scenario ? getScenariosByCategory(scenario.categoryId) : [];
  const currentIndex = categoryScenarios.findIndex((s) => s.id === scenarioId);
  const nextScenario = currentIndex >= 0 && currentIndex < categoryScenarios.length - 1
    ? categoryScenarios[currentIndex + 1]
    : null;

  if (!scenario) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="text-5xl mb-4">😕</div>
        <h2 className="font-display text-xl font-bold text-gray-700 mb-2">
          Scenario not found
        </h2>
        <button
          onClick={() => navigate('/library')}
          className="mt-4 text-primary-600 font-semibold text-sm"
        >
          Back to Library
        </button>
      </div>
    );
  }

  const handleToggleSave = () => {
    if (scenarioId) toggleSavedScenario(scenarioId);
  };

  const handleMarkPracticed = () => {
    if (scenarioId && !practiced) {
      markScenarioPracticed(scenarioId);
      setPracticed(true);
    }
  };

  /* ---- KID MODE VIEW ---- */
  if (isKidMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 pb-24">
        {/* Header */}
        <div
          className={`bg-gradient-to-br ${category?.color ?? 'from-primary-500 to-violet-600'} px-4 pt-12 pb-6`}
        >
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm font-semibold"
          >
            <ChevronLeft size={18} /> Back
          </button>
          <div className="text-6xl mb-3 text-center">{category?.icon}</div>
          <h1 className="font-display text-2xl font-extrabold text-white text-center leading-tight">
            {scenario.title}
          </h1>
        </div>

        <div className="px-4 pt-5 space-y-4 max-w-2xl mx-auto">
          {/* Kid scenario */}
          <div className="bg-white rounded-3xl p-5 shadow-card">
            <p className="text-gray-700 text-base leading-relaxed">
              {scenario.kidVersion || scenario.scenarioDescription}
            </p>
          </div>

          {/* Top 3 action steps */}
          <div className="space-y-3">
            {scenario.actionSteps.slice(0, 3).map((step) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: step.step * 0.1 }}
                className="flex items-start gap-3 bg-white rounded-2xl p-4 shadow-card"
              >
                <div className="w-8 h-8 rounded-full bg-primary-600 text-white font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                  {step.step}
                </div>
                <p className="text-gray-700 font-semibold text-sm pt-1">
                  {step.text}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Memory phrase */}
          <div className="bg-gradient-to-br from-primary-600 to-violet-700 rounded-3xl p-5 text-center shadow-lg">
            <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-2">
              Remember
            </p>
            <p className="font-display text-xl font-extrabold text-white leading-snug">
              "{scenario.memoryPhrase}"
            </p>
          </div>

          {/* Mark as practiced */}
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleMarkPracticed}
            disabled={practiced}
            className={`w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
              practiced
                ? 'bg-green-100 text-green-700'
                : 'bg-green-500 text-white shadow-md hover:bg-green-600'
            }`}
          >
            <CheckCircle size={20} />
            {practiced ? 'Great job! Practised!' : "I've Practised This!"}
          </motion.button>

          {practiced && (
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 flex items-center justify-center gap-1 py-3 rounded-2xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
              {nextScenario && (
                <button
                  onClick={() => navigate(`/library/scenario/${nextScenario.id}`)}
                  className="flex-1 flex items-center justify-center gap-1 py-3 rounded-2xl bg-primary-600 text-white font-bold text-sm hover:bg-primary-700 transition-colors"
                >
                  Next Scenario <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ---- PARENT MODE VIEW ---- */
  return (
    <div className="min-h-screen bg-cream pb-24">
      {/* Category header */}
      <div
        className={`bg-gradient-to-br ${category?.color ?? 'from-primary-500 to-violet-600'} px-4 pt-12 pb-6`}
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/80 hover:text-white text-sm font-semibold"
          >
            <ChevronLeft size={18} /> Back
          </button>
          <button
            onClick={handleToggleSave}
            className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
          >
            {isSaved ? (
              <BookmarkCheck size={14} />
            ) : (
              <Bookmark size={14} />
            )}
            {isSaved ? 'Saved' : 'Save'}
          </button>
        </div>

        {/* Category badge */}
        {category && (
          <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
            {category.icon} {category.name}
          </span>
        )}

        <h1 className="font-display text-2xl font-extrabold text-white leading-tight mb-2">
          {scenario.title}
        </h1>
        <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
          Ages {scenario.ageGroup}
        </span>
      </div>

      <div className="px-4 pt-5 space-y-4 max-w-2xl mx-auto">
        {/* Scenario description */}
        <div className="bg-white rounded-3xl p-5 shadow-card">
          <h2 className="font-display font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">
            The Scenario
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed">
            {scenario.scenarioDescription}
          </p>
        </div>

        {/* Parent prompt */}
        <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 flex gap-3">
          <MessageCircle
            size={18}
            className="text-primary-500 flex-shrink-0 mt-0.5"
          />
          <div>
            <p className="text-xs font-bold text-primary-600 uppercase tracking-wide mb-1">
              Ask your child
            </p>
            <p className="text-primary-800 text-sm leading-relaxed font-medium">
              {scenario.parentPrompt}
            </p>
          </div>
        </div>

        {/* Reveal button */}
        {!revealed && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => setRevealed(true)}
            className="w-full bg-primary-600 text-white py-4 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
          >
            <Eye size={20} />
            Reveal Answer
            <ChevronDown size={18} />
          </motion.button>
        )}

        {/* Revealed answer section */}
        <AnimatePresence>
          {revealed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: 'spring', stiffness: 240, damping: 24 }}
              className="space-y-4"
            >
              {/* Summary answer */}
              <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">
                  The Answer
                </p>
                <p className="text-green-900 text-sm leading-relaxed">
                  {scenario.revealAnswer}
                </p>
              </div>

              {/* Action steps */}
              <div className="bg-white rounded-3xl p-5 shadow-card">
                <h3 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide mb-4">
                  Action Steps
                </h3>
                <div className="space-y-3">
                  {scenario.actionSteps.map((step) => (
                    <div key={step.step} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-primary-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                        {step.step}
                      </div>
                      <p className="text-gray-700 text-sm pt-1 leading-snug">
                        {step.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* What child can say */}
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
                <MessageCircle
                  size={18}
                  className="text-blue-500 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">
                    What your child can say
                  </p>
                  <p className="text-blue-900 text-sm font-medium italic leading-relaxed">
                    {scenario.whatChildCanSay}
                  </p>
                </div>
              </div>

              {/* Parent coaching tip */}
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
                <Lightbulb
                  size={18}
                  className="text-amber-500 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1">
                    Parent coaching tip
                  </p>
                  <p className="text-amber-900 text-sm leading-relaxed">
                    {scenario.parentCoachingTip}
                  </p>
                </div>
              </div>

              {/* Practice together */}
              <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 flex gap-3">
                <Users
                  size={18}
                  className="text-violet-500 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-xs font-bold text-violet-600 uppercase tracking-wide mb-1">
                    Practice together
                  </p>
                  <p className="text-violet-900 text-sm leading-relaxed">
                    {scenario.practiceTogetherPrompt}
                  </p>
                </div>
              </div>

              {/* Memory phrase */}
              <div className="bg-gradient-to-br from-primary-600 to-violet-700 rounded-3xl p-5 text-center shadow-lg">
                <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-2">
                  Memory Phrase
                </p>
                <p className="font-display text-xl font-extrabold text-white leading-snug">
                  "{scenario.memoryPhrase}"
                </p>
              </div>

              {/* Mark as practiced */}
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleMarkPracticed}
                disabled={practiced}
                className={`w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
                  practiced
                    ? 'bg-green-100 text-green-700'
                    : 'bg-green-500 text-white shadow-md hover:bg-green-600'
                }`}
              >
                <CheckCircle size={20} />
                {practiced ? 'Marked as Practised!' : 'Mark as Practised'}
              </motion.button>

              {practiced && (
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex-1 flex items-center justify-center gap-1 py-3 rounded-2xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                  {nextScenario && (
                    <button
                      onClick={() => navigate(`/library/scenario/${nextScenario.id}`)}
                      className="flex-1 flex items-center justify-center gap-1 py-3 rounded-2xl bg-primary-600 text-white font-bold text-sm hover:bg-primary-700 transition-colors"
                    >
                      Next Scenario <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
